import { useEffect, useRef, useState } from "react";

/**
 * FalajBackdrop — code-rendered animated backdrop.
 *
 * Sits on TOP of a background image or video: everything is drawn on a
 * transparent canvas with `mix-blend-mode: screen` so the green lines and
 * light points glow additively over whatever image is behind.
 *
 * Runtime:
 *   • Pure SVG + SMIL + CSS animations — no requestAnimationFrame loop.
 *   • Seamless 10-second loop (all durations divide 10s cleanly).
 *   • Animates on mobile too, but in a reduced-complexity mode: fewer
 *     channels, fewer light points, fewer khatam cells, longer SMIL cycles.
 *   • Respects prefers-reduced-motion → renders a single static frame.
 *   • Pauses off-screen (IntersectionObserver) and on tab-hidden.
 *   • External `paused` prop is driven by the hero's play/pause toggle.
 */

// Media hook — reduced-complexity mobile mode ↔ full desktop mode.
function useMobileMedia() {
  const [mobile, setMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setMobile(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return mobile;
}

// --- Channel paths (viewBox 1920 x 1080) ------------------------------------
const CH = {
  a: "M -40 700 C 240 660 460 720 720 690 S 1180 620 1420 640 S 1780 700 1980 660",
  b: "M 420 900 C 620 820 720 780 900 760 S 1220 720 1420 640",
  c: "M 720 690 C 900 680 1040 760 1240 780 S 1560 820 1980 820",
  d: "M 900 760 C 1080 820 1200 900 1400 920 S 1700 940 1980 900",
};

// Full-desktop scene.
const CHANNELS_FULL = [
  { id: "a", sw: 1.4, op: 0.5, mid: 3, midOp: 0.55 },
  { id: "b", sw: 1.2, op: 0.42, mid: 3, midOp: 0.5 },
  { id: "c", sw: 1.2, op: 0.42, mid: 3, midOp: 0.5 },
  { id: "d", sw: 1.1, op: 0.38, mid: 3, midOp: 0.5 },
];
const POINTS_FULL = [
  { path: "a", dur: 10, begin: 0,  r: 3,   fill: "#B7E9B7" },
  { path: "a", dur: 10, begin: -4, r: 2.4, fill: "#DFF7C4" },
  { path: "b", dur: 10, begin: -2, r: 2.6, fill: "#B7E9B7" },
  { path: "c", dur: 10, begin: -6, r: 2.4, fill: "#B7E9B7" },
  { path: "d", dur: 10, begin: -8, r: 2.2, fill: "#DFF7C4" },
  { path: "c", dur: 10, begin: -5, r: 2,   fill: "#9ABC04" },
];
const STARS_FULL = [
  { x: 1080, y: 220, s: 1.0,  d: 0.4 },
  { x: 1360, y: 300, s: 0.75, d: 2.1 },
  { x: 1620, y: 200, s: 0.9,  d: 5.6 },
  { x: 1780, y: 420, s: 0.85, d: 7.9 },
  { x: 1200, y: 480, s: 0.7,  d: 1.3 },
  { x: 1500, y: 520, s: 1.05, d: 4.4 },
  { x: 900,  y: 380, s: 0.65, d: 8.7 },
  { x: 1660, y: 720, s: 0.9,  d: 3.2 },
  { x: 1340, y: 860, s: 0.75, d: 6.5 },
  { x: 1040, y: 940, s: 0.6,  d: 9.1 },
  { x: 640,  y: 940, s: 0.7,  d: 2.7 },
  { x: 380,  y: 860, s: 0.6,  d: 5.9 },
  { x: 1820, y: 900, s: 0.55, d: 0.8 },
];
const NODES = [
  { cx: 720,  cy: 690, delay: 0 },
  { cx: 900,  cy: 760, delay: 2.5 },
  { cx: 1240, cy: 780, delay: 5 },
  { cx: 1420, cy: 640, delay: 7.5, accent: true },
];

// Reduced-complexity mobile scene.
const CHANNELS_MOBILE = [
  { id: "a", sw: 1.4, op: 0.55, mid: 2.6, midOp: 0.55 },
  { id: "c", sw: 1.2, op: 0.45, mid: 2.6, midOp: 0.5 },
];
const POINTS_MOBILE = [
  { path: "a", dur: 14, begin: 0,  r: 2.8, fill: "#B7E9B7" },
  { path: "c", dur: 14, begin: -7, r: 2.4, fill: "#B7E9B7" },
  { path: "c", dur: 14, begin: -4, r: 2,   fill: "#9ABC04" },
];
const STARS_MOBILE = [
  { x: 1360, y: 300, s: 0.9,  d: 2.1 },
  { x: 1620, y: 200, s: 1.0,  d: 5.6 },
  { x: 1200, y: 480, s: 0.85, d: 1.3 },
  { x: 1500, y: 820, s: 1.1,  d: 4.4 },
  { x: 900,  y: 380, s: 0.85, d: 8.7 },
];
const NODES_MOBILE = NODES.slice(0, 2);

export function FalajBackdrop({ paused = false, className = "" }) {
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const inViewRef = useRef(true);
  const tabVisibleRef = useRef(true);
  const staticModeRef = useRef(false);

  const mobile = useMobileMedia();
  const channels = mobile ? CHANNELS_MOBILE : CHANNELS_FULL;
  const points   = mobile ? POINTS_MOBILE   : POINTS_FULL;
  const stars    = mobile ? STARS_MOBILE    : STARS_FULL;
  const nodes    = mobile ? NODES_MOBILE    : NODES;

  const applyRunState = () => {
    const svg = svgRef.current;
    const wrap = wrapRef.current;
    if (!svg || !wrap) return;
    const shouldRun =
      !staticModeRef.current &&
      !paused &&
      inViewRef.current &&
      tabVisibleRef.current;
    try {
      if (shouldRun) svg.unpauseAnimations();
      else svg.pauseAnimations();
    } catch (_) { /* CSS fallback below */ }
    wrap.classList.toggle("falaj-paused", !shouldRun);
  };

  // Prefers-reduced-motion → freeze entirely.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      staticModeRef.current = reduce.matches;
      applyRunState();
    };
    update();
    reduce.addEventListener?.("change", update);
    return () => reduce.removeEventListener?.("change", update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Off-screen pause. Uses a small negative rootMargin so the resume fires
  // just before the hero re-enters the viewport (rather than exactly at the
  // 1% threshold, which some browsers debounce), and multiple thresholds so
  // the callback runs at both edges of intersection.
  useEffect(() => {
    if (typeof window === "undefined" || !wrapRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        inViewRef.current = entry ? entry.isIntersecting || entry.intersectionRatio > 0 : true;
        applyRunState();
      },
      { threshold: [0, 0.05, 0.25], rootMargin: "50px 0px 50px 0px" },
    );
    io.observe(wrapRef.current);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tab-hidden pause.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => {
      tabVisibleRef.current = !document.hidden;
      applyRunState();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // External paused prop, and re-apply when mobile flips.
  useEffect(() => { applyRunState(); /* eslint-disable-next-line */ }, [paused, mobile]);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      data-testid="falaj-backdrop"
      style={{ mixBlendMode: "screen" }}
      className={`pointer-events-none absolute inset-0 ${className}`}
    >
      <style>{`
        @keyframes falaj-star-pulse {
          0%, 84%, 100% { opacity: 0.07; }
          88% { opacity: 0.34; }
          92% { opacity: 0.18; }
        }
        @keyframes falaj-ring {
          0%   { r: 6;  opacity: 0.55; }
          85%  { r: 34; opacity: 0; }
          100% { r: 34; opacity: 0; }
        }
        .falaj-star { animation: falaj-star-pulse 10s linear infinite; transform-origin: center; transform-box: fill-box; }
        .falaj-ring { animation: falaj-ring 5s ease-out infinite; }
        .falaj-paused .falaj-star,
        .falaj-paused .falaj-ring { animation-play-state: paused !important; }
        @media (max-width: 767px) {
          [data-testid="falaj-backdrop"] { opacity: 0.85; }
        }
      `}</style>

      <svg
        ref={svgRef}
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid slice"
        className="h-full w-full"
      >
        <defs>
          <filter id="falaj-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <symbol id="falaj-khatam" viewBox="-30 -30 60 60">
            <g fill="none" stroke="#58AF58" strokeWidth="1.2">
              <rect x="-22" y="-22" width="44" height="44" />
              <rect x="-22" y="-22" width="44" height="44" transform="rotate(45)" />
              <circle cx="0" cy="0" r="4" />
            </g>
          </symbol>
          <path id="falaj-ch-a" d={CH.a} />
          <path id="falaj-ch-b" d={CH.b} />
          <path id="falaj-ch-c" d={CH.c} />
          <path id="falaj-ch-d" d={CH.d} />
        </defs>

        {/* Khatam stars */}
        <g>
          {stars.map((s, i) => (
            <g
              key={i}
              className="falaj-star"
              style={{ animationDelay: `-${s.d}s`, transformOrigin: `${s.x}px ${s.y}px` }}
              transform={`translate(${s.x} ${s.y}) scale(${s.s})`}
            >
              <use href="#falaj-khatam" x="-30" y="-30" width="60" height="60" />
            </g>
          ))}
        </g>

        {/* Channel strokes — mid-tone underlay + brand-green line */}
        <g fill="none" strokeLinecap="round">
          {channels.map((c) => (
            <g key={c.id}>
              <use href={`#falaj-ch-${c.id}`} stroke="#19532B" strokeWidth={c.mid} opacity={c.midOp} />
              <use href={`#falaj-ch-${c.id}`} stroke="#58AF58" strokeWidth={c.sw} opacity={c.op} />
            </g>
          ))}
        </g>

        {/* Travelling light points */}
        <g filter="url(#falaj-glow)">
          {points.map((p, i) => (
            <circle key={i} r={p.r} fill={p.fill}>
              <animateMotion
                dur={`${p.dur}s`}
                begin={`${p.begin}s`}
                repeatCount="indefinite"
                rotate="auto"
              >
                <mpath href={`#falaj-ch-${p.path}`} />
              </animateMotion>
            </circle>
          ))}
        </g>

        {/* Nodes with expanding glow rings */}
        <g>
          {nodes.map((n, i) => (
            <g key={i}>
              <circle
                className="falaj-ring"
                cx={n.cx}
                cy={n.cy}
                r="6"
                fill="none"
                stroke={n.accent ? "#9ABC04" : "#58AF58"}
                strokeWidth="1"
                style={{ animationDelay: `-${n.delay}s` }}
              />
              <circle
                cx={n.cx}
                cy={n.cy}
                r="2.6"
                fill={n.accent ? "#9ABC04" : "#B7E9B7"}
                opacity="0.9"
              />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}

export default FalajBackdrop;
